import { useEffect, useMemo } from 'react';
import { Model } from '@shared/types';
import { ModelConfig } from '@/types/misc';
import { IMAGE_TO_CAD_MODELS, PARAMETRIC_MODELS } from '@/lib/utils';
import { isImageTier } from '@/components/textAreaChat/useImageModelLock';
import { useHomepage } from '@/contexts/homepage-context';

// The image-to-3D tiers (Magneto 0.1 / 0.2 / 0.3 / Trellis.2 / Hunyuan /
// Tripo), surfaced at the TOP of the "Generative Model" dropdown when "Text or
// Image to CAD" is selected.
const IMAGE_TIER_MODELS = IMAGE_TO_CAD_MODELS.filter((m) => isImageTier(m.id));
const DEFAULT_IMAGE_TIER: Model = 'meshy7';

/**
 * Pure: the sidebar "Generative Model" dropdown list.
 * - "Text or Image to CAD" selected: image tiers at the top, then all the LLMs
 *   — every option available.
 * - …once an image is uploaded (any count): ONLY the image tiers (both stay
 *   selectable — multiple images are allowed for either tier).
 * - Any other domain: unchanged (the LLM list, or the full creative list when
 *   the conversation is already in imagetocad).
 */
export function buildImageToCadModelList(
  textToCadSelected: boolean,
  imageUploaded: boolean,
  type: 'parametric' | 'imagetocad',
): ModelConfig[] {
  if (!textToCadSelected) {
    return type === 'imagetocad' ? IMAGE_TO_CAD_MODELS : PARAMETRIC_MODELS;
  }
  if (imageUploaded) return IMAGE_TIER_MODELS;
  return [...IMAGE_TIER_MODELS, ...PARAMETRIC_MODELS];
}

/**
 * Drives the sidebar model dropdown for "Text or Image to CAD": surfaces the
 * image tiers and, once an image is attached, locks to them (defaulting to
 * Magneto 0.1). It ONLY ever changes the selected `model` — never `type` and
 * nothing else in the feature tree. Generation routes off the model (see
 * `TextToCadSection`'s derived conversation type), so the parametric sidebar UI
 * (Add Image/Schematic button, prompt text, kernel) is left completely intact.
 */
export function useImageToCadModels(): ModelConfig[] {
  const { textToCadSelected, imageUploaded, model, type, setModel } =
    useHomepage();

  // Uploading image(s) locks the picker to the image tiers; default Magneto 0.1.
  useEffect(() => {
    if (textToCadSelected && imageUploaded && !isImageTier(model)) {
      setModel(DEFAULT_IMAGE_TIER);
    }
  }, [textToCadSelected, imageUploaded, model, setModel]);

  return useMemo(
    () => buildImageToCadModelList(textToCadSelected, imageUploaded, type),
    [textToCadSelected, imageUploaded, type],
  );
}
